import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Monitor } from 'lucide-react';
import baseapi from '../../services/baseapi';

import OverviewTab from './tabs/OverviewTab';
import PodsTab from './tabs/PodsTab';
import WorkloadsTab from './tabs/WorkloadsTab';
import ServicesTab from './tabs/ServicesTab';
import AlertsTab from './tabs/AlertsTab';
import EventsTab from './tabs/EventsTab';
import HistoryTab from './tabs/HistoryTab';

const KubernetesTab: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const clientId = searchParams.get('client_id') || '';
  const urlTab = searchParams.get('tab') || 'overview';
  const navigate = useNavigate();
  const [clusterStatus, setClusterStatus] = useState('HEALTHY');
  const [tabCounts, setTabCounts] = useState<{ [key: string]: number | undefined }>({});

  // We can fetch just the overview once in the parent to get the global cluster status
  useEffect(() => {
    if (!agentId) return;
    const fetchStatus = async () => {
      try {
        const clusterName = searchParams.get('cluster_name');
        const params: any = { agent_id: agentId, client_id: clientId };
        if (clusterName) {
          params.cluster_name = clusterName;
        }
        const snapRes = await baseapi.get('/k8s_dashboard/snapshot', { params });
        if (snapRes.data?.status && snapRes.data?.data) {
          const data = snapRes.data.data;
          setClusterStatus(data.status || 'HEALTHY');

          let workloadsCount = 0;
          if (data.workloads) {
            workloadsCount = (data.workloads.deployments?.length || 0) +
              (data.workloads.daemonsets?.length || 0) +
              (data.workloads.statefulsets?.length || 0) +
              (data.workloads.jobs?.length || 0) +
              (data.workloads.cronjobs?.length || 0);
          }

          setTabCounts({
            pods: data.summary?.pods_total ?? data.pods?.length,
            workloads: workloadsCount > 0 ? workloadsCount : undefined,
            netstore: data.summary?.services_total ?? data.services?.length,
            events: data.events?.length,
            alerts: data.alerts?.length
          });
        }
      } catch (e) {
        setClusterStatus('UNREACHABLE');
      }
    };
    fetchStatus();
  }, [agentId, clientId, searchParams]);

  const tabs = [
    { id: 'overview', label: 'OVERVIEW' },
    { id: 'pods', label: 'PODS', count: tabCounts.pods },
    { id: 'workloads', label: 'WORKLOADS', count: tabCounts.workloads },
    { id: 'netstore', label: 'SERVICES & STORAGE', count: tabCounts.netstore },
    { id: 'alerts', label: 'ALERTS' , count: tabCounts.alerts},
    { id: 'events', label: 'EVENTS', count: tabCounts.events },
    { id: 'history', label: 'HISTORY' },
  ];


  return (
    <div className="bg-gray-900 min-h-screen text-gray-100 font-sans pb-20">
      {/* Header */}
      <header className="flex items-center gap-2 p-4 border-b border-gray-800 bg-gray-900/50 sticky top-0 z-50 backdrop-blur-md">
        <button
          onClick={() => navigate('/kubernetes')}
          className="flex items-center gap-1.5 px-2 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 hover:text-white rounded-lg transition-colors text-xs font-bold tracking-wide"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
        </button>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Monitor className="w-5 h-5 text-orange-500" />
            <div className="font-mono font-bold text-sm tracking-wide uppercase">
              K8S MONITORING
            </div>
          </div>

          <span className={`font-mono text-[10px] font-bold px-3 py-1 rounded-full tracking-wider ${clusterStatus === 'HEALTHY' ? 'bg-green-500/20 text-green-400' :
            clusterStatus === 'DEGRADED' ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-red-500/20 text-red-400'
            }`}>
            {clusterStatus}
          </span>
        </div>

        
      </header>

      {/* Navigation */}
      <nav className="flex gap-1 px-4 bg-gray-900 border-b border-gray-800 overflow-x-auto custom-scroll">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              setSearchParams(prev => {
                const nextParams = new URLSearchParams(prev);
                nextParams.set('tab', tab.id);
                return nextParams;
              });
            }}
            className={`flex items-center gap-2 whitespace-nowrap px-4 py-3.5 font-sans font-bold text-[12px] tracking-wide transition-all border-b-2 outline-none ${urlTab === tab.id
              ? 'text-orange-500 border-orange-500'
              : 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-white/5 rounded-t-md'
              }`}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className={`px-2 py-0.5 rounded-full font-mono text-[11px] font-extrabold transition-colors ${urlTab === tab.id
                ? 'bg-orange-500 text-white'
                : 'bg-white/10 text-gray-200'
                }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </nav>

      {/* Main Content */}
      <main className="p-4 mx-auto mt-1">
        {urlTab === 'overview' && <OverviewTab agentId={agentId!} clientId={clientId} />}
        {urlTab === 'pods' && <PodsTab agentId={agentId!} clientId={clientId} />}
        {urlTab === 'workloads' && <WorkloadsTab agentId={agentId!} clientId={clientId} />}
        {urlTab === 'netstore' && <ServicesTab agentId={agentId!} clientId={clientId} />}
        {urlTab === 'alerts' && <AlertsTab agentId={agentId!} clientId={clientId} />}
        {urlTab === 'events' && <EventsTab agentId={agentId!} clientId={clientId} />}
        {urlTab === 'history' && <HistoryTab agentId={agentId!} clientId={clientId} />}
      </main>
    </div>
  );
};

export default KubernetesTab;
